import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../../Components/common/Navbar";
import Footer from "../../Components/common/Footer";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faUsers,
  faPlay,
  faArrowRight,
  faFire,
  faUser,
  faSearch,
  faCheckCircle,
  faGraduationCap,
} from "@fortawesome/free-solid-svg-icons";
import { courseService } from "../../api/course.service";
import { learningService } from "../../api/learning.service";
import { API_BASE_URL } from "../../api/constant";

const FALLBACK_IMG = "https://placehold.co/600x400/1d7270/ffffff?text=Course";

const getCourseImg = (course) =>
  course.hasImage
    ? `${API_BASE_URL}/api/courses/${course.course_id}/course-image`
    : FALLBACK_IMG;

function Courses() {
  const [courses, setCourses] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrollingId, setEnrollingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterBy, setFilterBy] = useState("all");
  const [displayCount, setDisplayCount] = useState(6);
  const [heroVisible, setHeroVisible] = useState(false);

  const userId = localStorage.getItem("id");
  const authToken = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const coursesRes = await courseService.getAllCourses();
        if (coursesRes.success && Array.isArray(coursesRes.data)) {
          setCourses(coursesRes.data);
        }

        if (userId) {
          const enrollmentsRes = await learningService.getEnrollments(userId);
          if (enrollmentsRes.success && Array.isArray(enrollmentsRes.data)) {
            setEnrolled(enrollmentsRes.data.map((item) => item.course_id));
          }
        }
      } catch (err) {
        console.error("Error loading courses:", err);
        message.error("Failed to load courses. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const filteredAndSortedCourses = useMemo(() => {
    let filtered = courses.filter((course) => {
      const name = course.course_name?.toLowerCase() ?? "";
      const instructor = course.instructor?.toLowerCase() ?? "";
      const matchesSearch =
        name.includes(searchTerm.toLowerCase()) ||
        instructor.includes(searchTerm.toLowerCase());

      if (filterBy === "enrolled")
        return matchesSearch && enrolled.includes(course.course_id);
      if (filterBy === "available")
        return matchesSearch && !enrolled.includes(course.course_id);
      return matchesSearch;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.course_name ?? "").localeCompare(b.course_name ?? "");
        case "instructor":
          return (a.instructor ?? "").localeCompare(b.instructor ?? "");
        case "price":
          return (Number(a.price) || 0) - (Number(b.price) || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [courses, searchTerm, sortBy, filterBy, enrolled]);

  const displayedCourses = filteredAndSortedCourses.slice(0, displayCount);

  const enrollCourse = async (courseId) => {
    if (!authToken) {
      message.error("You need to login to enroll");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }

    setEnrollingId(courseId);
    try {
      const res = await learningService.enrollCourse(userId, courseId);

      if (res.success) {
        if (res.data === "Enrolled successfully") {
          setEnrolled((prev) => [...prev, courseId]);
          message.success("Enrolled successfully!");
          setTimeout(() => navigate(`/course/${courseId}`), 1500);
        } else if (res.data === "Course already enrolled") {
          message.info("You are already enrolled in this course");
          navigate(`/course/${courseId}`);
        } else {
          message.error(res.data || "Failed to enroll. Please try again.");
        }
      } else {
        message.error("Enrollment failed. Please try again.");
      }
    } catch (err) {
      message.error("Something went wrong. Please try again.");
    } finally {
      setEnrollingId(null);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden">
      <Navbar page="courses" />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 flex items-center justify-center bg-gradient-to-br from-[#0a2e2d] via-primary-dark to-primary text-center">
        {/* dot grid */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />
        {/* decorative rings */}
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full border-[36px] border-secondary/20 pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-60 h-60 rounded-full border-[24px] border-warning/15 pointer-events-none" />
        {/* blobs */}
        <div className="absolute top-10 left-10 w-48 h-48 bg-secondary/15 rounded-full blur-3xl animate-float pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-56 h-56 bg-warning/10 rounded-full blur-3xl animate-float-slow pointer-events-none" />

        <div className={`relative z-10 px-6 max-w-3xl mx-auto transition-all duration-1000 ease-out ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-white/85 text-sm font-medium mb-6">
            <FontAwesomeIcon icon={faGraduationCap} className="text-warning text-xs" />
            Full Course Catalog
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-5">
            Explore &amp; Start
            <span className="block mt-2 bg-gradient-to-r from-warning via-yellow-300 to-secondary bg-clip-text text-transparent">
              Learning Today
            </span>
          </h1>
          <p className="text-white/75 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Browse our full catalog — find the perfect course and start building your skills today.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* ── Search & Filters ── */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6 mb-10 -mt-8 relative z-10">
          <div className="flex flex-col md:flex-row gap-4">

            {/* Search */}
            <div className="flex-1 relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                placeholder="Search courses or instructors…"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setDisplayCount(6); }}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all duration-200 bg-gray-50 text-sm"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none pl-4 pr-10 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-gray-50 text-sm transition-all duration-200 cursor-pointer font-medium text-gray-600"
            >
              <option value="name">Sort: Name</option>
              <option value="instructor">Sort: Instructor</option>
              <option value="price">Sort: Price</option>
            </select>

            {/* Filter */}
            <select
              value={filterBy}
              onChange={(e) => { setFilterBy(e.target.value); setDisplayCount(6); }}
              className="appearance-none pl-4 pr-10 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-gray-50 text-sm transition-all duration-200 cursor-pointer font-medium text-gray-600"
            >
              <option value="all">All Courses</option>
              <option value="available">Not Enrolled</option>
              <option value="enrolled">My Courses</option>
            </select>
          </div>

          {/* Result count + clear */}
          <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
            <span>
              Showing{" "}
              <span className="font-bold text-gray-700">{displayedCourses.length}</span>{" "}
              of{" "}
              <span className="font-bold text-gray-700">{filteredAndSortedCourses.length}</span>{" "}
              courses
            </span>
            {(searchTerm || filterBy !== "all") && (
              <button
                onClick={() => { setSearchTerm(""); setFilterBy("all"); setDisplayCount(6); }}
                className="text-primary hover:text-primary-dark font-semibold transition-colors"
              >
                Reset filters ×
              </button>
            )}
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="flex flex-col justify-center items-center h-64 gap-4">
            <div className="w-12 h-12 border-[3px] border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-500 text-sm">Loading courses…</p>
          </div>

        ) : filteredAndSortedCourses.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-64 text-center gap-3">
            <div className="w-16 h-16 rounded-2xl bg-primary-light flex items-center justify-center mb-2">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <p className="text-gray-700 font-semibold text-lg">No courses found</p>
            <p className="text-gray-400 text-sm">Try adjusting your search or filter</p>
            <button
              onClick={() => { setSearchTerm(""); setFilterBy("all"); }}
              className="mt-1 px-6 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-dark transition-colors"
            >
              Reset filters
            </button>
          </div>

        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayedCourses.map((course) => {
                const isEnrolled = enrolled.includes(course.course_id);
                const isEnrolling = enrollingId === course.course_id;
                const isFree = !course.price || Number(course.price) === 0;

                return (
                  <div
                    key={course.course_id}
                    onClick={() => isEnrolled ? navigate(`/course/${course.course_id}`) : enrollCourse(course.course_id)}
                    className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer"
                  >
                    {/* ── Image area ── */}
                    <div className="relative overflow-hidden h-52">
                      <img
                        src={getCourseImg(course)}
                        alt={course.course_name}
                        onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMG; }}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                      {/* Enrolled badge — top left */}
                      {isEnrolled && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
                            Enrolled
                          </span>
                        </div>
                      )}

                      {/* Price badge — top right */}
                      <div className="absolute top-4 right-4">
                        <span className="bg-warning text-black text-xs font-extrabold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                          <FontAwesomeIcon icon={faFire} className="text-orange-500 text-[10px]" />
                          {isFree ? "FREE" : `₹${course.price}`}
                        </span>
                      </div>

                      {/* Instructor — bottom left over image */}
                      <div className="absolute bottom-4 left-4 flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-full bg-primary-light flex items-center justify-center shrink-0">
                          <FontAwesomeIcon icon={faUser} className="text-primary text-[9px]" />
                        </div>
                        <span className="text-white text-xs font-semibold drop-shadow">{course.instructor}</span>
                      </div>
                    </div>

                    {/* ── Card body ── */}
                    <div className="p-6">
                      {/* Stars */}
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, s) => (
                          <FontAwesomeIcon key={s} icon={faStar} className="text-warning text-xs" />
                        ))}
                        <span className="text-gray-400 text-xs ml-2 font-medium">(4.9)</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-left font-extrabold text-gray-900 text-lg leading-snug mb-4 group-hover:text-primary transition-colors duration-300 line-clamp-2">
                        {course.course_name?.length < 8
                          ? `${course.course_name} Tutorial`
                          : course.course_name}
                      </h3>

                      {/* Divider + action */}
                      <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-gray-400 text-xs font-medium">
                          <FontAwesomeIcon icon={faUsers} className="text-gray-300" />
                          <span>Students enrolled</span>
                        </div>

                        {isEnrolled ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate("/Learnings"); }}
                            className="flex items-center gap-2 bg-primary-light text-primary border border-primary/20 rounded-full px-4 py-2 text-xs font-bold hover:bg-primary hover:text-white transition-all duration-300 group-hover:scale-105"
                          >
                            <FontAwesomeIcon icon={faPlay} className="text-[9px]" />
                            Continue
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); enrollCourse(course.course_id); }}
                            disabled={isEnrolling}
                            className="flex items-center gap-2 bg-primary text-white rounded-full px-5 py-2 text-xs font-bold hover:bg-primary-dark transition-all duration-300 shadow-sm group-hover:shadow-lg group-hover:shadow-primary/30 group-hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                          >
                            {isEnrolling ? (
                              <>
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Enrolling…
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                                Enroll Now
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {displayedCourses.length < filteredAndSortedCourses.length && (
              <div className="text-center mt-12">
                <button
                  onClick={() => setDisplayCount((prev) => prev + 6)}
                  className="px-8 py-3.5 bg-white border-2 border-primary/20 text-primary font-bold rounded-full hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 text-sm hover:scale-105 shadow-sm"
                >
                  Load More Courses
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default Courses;
